const API_URL = process.env.REACT_APP_API_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) throw new Error(`Erreur ${response.status}`);
  if (response.status === 204) return null;
  return response.json();
}

function jsonBody(data) {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
}

// ─── Projets ──────────────────────────────────────────────────────────────────

export async function getProjets(page = 1, limit = 10, search = "", filters = {}, tri = "date_debut", ordre = "DESC") {
  const {
    statuses    = [],
    clientId    = null,
    dateDebut   = null,
    dateFin     = null,
    dateCloture = null,
  } = filters;

  const conditions = [];

  // Titre (barre de recherche rapide)
  if (search && search.trim().length >= 1) {
    const formatted = search.trim().replace(/'/g, "''");
    conditions.push(`titre:'${formatted}'`);
  }

  // Statuts
  if (statuses.length > 0) {
    const parts = statuses.map(s => `status:'${s}'`);
    conditions.push(parts.length > 1 ? `(${parts.join(" OR ")})` : parts[0]);
  }

  // Client (id exact)
  if (clientId) {
    conditions.push(`client:'${clientId}'`);
  }

  // Dates — syntaxe : field:op'value'
  if (dateDebut?.val)   conditions.push(`date_debut:${dateDebut.op}'${dateDebut.val}'`);
  if (dateFin?.val)     conditions.push(`date_fin:${dateFin.op}'${dateFin.val}'`);
  if (dateCloture?.val) conditions.push(`date_cloture:${dateCloture.op}'${dateCloture.val}'`);

  const params = new URLSearchParams({ page, limite: limit });
  if (conditions.length > 0) params.set("requete", conditions.join(" AND "));
  if (tri)                   params.set("tri", tri);
  if (tri)                   params.set("ordre", ordre);

  const url = `${API_URL}/projet?${params.toString()}`;
  const response = await fetch(url, { method: "GET", credentials: "include" });
  if (!response.ok) throw new Error("Non authentifié");
  return response.json();
}
  

export async function updateProjet(id, projetData) {
  const response = await fetch(`${API_URL}/projet/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(projetData),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la mise à jour du projet");
  }

  return response.json();
}


export async function reopenProjet(id) {
  const response = await fetch(`${API_URL}/projet/${id}/reouvrir`, {
    method: "PUT",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la réouverture du projet");
  }

  return response.json();
}


export async function closeProjet(id) {
  const response = await fetch(`${API_URL}/projet/${id}/fermer`, {
    method: "PUT",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la fermeture du projet");
  }

  return response.json();
}


export async function getUsersByProjetId(projetId) {
  return apiFetch(`${API_URL}/projet/${projetId}/utilisateurs`);
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export async function createProjet(projetData) {
  return apiFetch(`${API_URL}/projet`, jsonBody(projetData));
}

export async function getProjetById(id) {
  return apiFetch(`${API_URL}/projet/${id}`);
}

export async function deleteProjet(id) {
  return apiFetch(`${API_URL}/projet/${id}`, { method: "DELETE" });
}

// ─── Étapes ───────────────────────────────────────────────────────────────────

export async function getProjetEtapes(id) {
  return apiFetch(`${API_URL}/projet/${id}/etapes`);
}

export async function createEtape(projetId, etapeData) {
  return apiFetch(`${API_URL}/projet/${projetId}/etape`, jsonBody(etapeData));
}

export async function updateEtape(projetId, stageId, etapeData) {
  return apiFetch(`${API_URL}/projet/${projetId}/etape/${stageId}`, {
    ...jsonBody(etapeData),
    method: "PUT",
  });
}

export async function deleteEtape(projetId, stageId) {
  return apiFetch(`${API_URL}/projet/${projetId}/etape/${stageId}`, { method: "DELETE" });
}

// ─── Interventions ────────────────────────────────────────────────────────────

export async function createIntervention(projetId, data) {
  return apiFetch(`${API_URL}/projet/${projetId}/intervention`, jsonBody(data));
}

export async function updateIntervention(projetId, intId, data) {
  return apiFetch(`${API_URL}/projet/${projetId}/intervention/${intId}`, {
    ...jsonBody(data),
    method: "PUT",
  });
}

export async function deleteIntervention(projetId, intId) {
  return apiFetch(`${API_URL}/projet/${projetId}/intervention/${intId}`, { method: "DELETE" });
}

// ─── Groupes ──────────────────────────────────────────────────────────────────

export async function getProjetGroupes(id) {
  return apiFetch(`${API_URL}/projet/${id}/groupes`);
}

export async function addProjetGroupes(id, groupeIds) {
  return apiFetch(`${API_URL}/projet/${id}/groupes`, jsonBody({ projet: { groupes: groupeIds } }));
}

export async function removeProjetGroupes(id, groupeIds) {
  return apiFetch(`${API_URL}/projet/${id}/groupes`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projet: { groupes: groupeIds } }),
  });
}

// ─── Utilisateurs ─────────────────────────────────────────────────────────────

export async function addProjetUtilisateurs(id, userIds) {
  return apiFetch(`${API_URL}/projet/${id}/utilisateurs`, jsonBody({ projet: { utilisateurs: userIds } }));
}

export async function removeProjetUtilisateurs(id, userIds) {
  return apiFetch(`${API_URL}/projet/${id}/utilisateurs`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projet: { utilisateurs: userIds } }),
  });
}

// ─── Fichiers ────────────────────────────────────────────────────────────────

export async function getProjetFichiers(id) {
  return apiFetch(`${API_URL}/projet/${id}/fichiers`);
}

export async function uploadProjetFichier(projetId, file) {
  const formData = new FormData();
  formData.append("fichier", file);
  return apiFetch(`${API_URL}/projet/${projetId}/fichier`, {
    method: "POST",
    body: formData,
  });
}

export async function downloadProjetFichier(projetId, fileId) {
  const response = await fetch(`${API_URL}/projet/${projetId}/fichier/${fileId}/telecharger`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération du fichier");
  }


  return await response.blob();
}
export async function viewProjetFichier(projetId, fileId) {
  const response = await fetch(
    `${API_URL}/projet/${projetId}/fichier/${fileId}/telecharger`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'ouverture du fichier");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  
  window.open(url, "_blank");
}

export async function deleteProjetFichier(projetId, fileId) {
  return apiFetch(`${API_URL}/projet/${projetId}/fichier/${fileId}`, { method: "DELETE" });
}
